import React, { FC } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import Button from 'components/common/ui-kit/Button';
import DocumentList from 'components/statements/DocumentList/view';
import FillingStatementsForm from 'components/statements/FillingStatementsForm';
import useAppDispatch from 'hooks/common/useAppDispatch';
import useFillingStatementsRoutesContext from 'hooks/statements/useFillingStatementsRoutesContext';
import terms from 'i18n';
import {
  DocumentTemplateType,
  DocumentType,
} from 'models/applications/application-documents';
import {
  ApplicationEducationLevel,
  ApplicationState,
} from 'models/applications/enum';
import { createToast, hideModal, showModal } from 'redux/actions';
import {
  applicationsApi,
  useTakeToDirectionsEditingMutation,
  useWithdrawApplicationMutation,
  useGetApplicationByIdQuery,
} from 'redux/api/applications/applications-api';
import { useGetStaffApplicationByIdQuery } from 'redux/api/staff/staff-applications-api';
import { useIsImpersonate } from 'utils/domain/impersonate';

import useDownloadFilesForm from '../../../hooks/statements/documents/useDownloadFilesForm';
import { useDownloadFormCheckboxes } from '../../../hooks/statements/documents/useDownloadFormCheckboxes';
import { applicationDocumentsApi } from '../../../redux/api/applications/application-documents-api';
import { downloadFile } from '../../../utils/common';
import Checkbox from '../../common/ui-kit/Checkbox';

const DownloadFilesForm: FC = () => {
  const { onNextPageNavigate } = useFillingStatementsRoutesContext();
  const isImpersonate = useIsImpersonate();

  const {
    register,
    fields,
    isAllCheckBoxesChecked,
    checkAllCheckboxes,
    onCheckboxChange,
    isDocumentsDisabled,
  } = useDownloadFormCheckboxes();

  const { documents, refetchDocuments, isFormDisabled } = useDownloadFilesForm({
    isAllCheckBoxesChecked,
    checkAllCheckboxes,
  });

  const { applicationId } = useParams();
  const [generateDocument] =
    applicationDocumentsApi.useLazyGenerateDocumentQuery();

  const { data: application, refetch } = useGetStaffApplicationByIdQuery(
    +applicationId!,
    {
      skip: applicationId === undefined && !isImpersonate,
    }
  );

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const applicationIdNumber = applicationId ? Number(applicationId) : undefined;

  const { data: userApplication } = useGetApplicationByIdQuery(
    applicationIdNumber as number,
    {
      skip: !applicationIdNumber && !isImpersonate,
    }
  );

  const [takeToDirectionEditingMutationFn] =
    useTakeToDirectionsEditingMutation();
  const [withdrawApplicationMutationFn] = useWithdrawApplicationMutation();
  const [takeBack] = applicationsApi.useTakeBackMutation();
  const navigateToDirections = () => navigate('../directions');
  const takeApplicationBack = () => {
    if (!application) return;
    takeBack(application.id)
      .unwrap()
      .then(() => {
        dispatch(createToast(terms.APPLICATION_TAKEN_FOR_EDITING, 'success'));
        refetch();
      })
      .catch(() => {
        dispatch(createToast(terms.APPLICATION_DELETION_ERROR, 'danger'));
      });
  };
  const onDirectionsEditingClick = () => {
    if (!application) return;
    dispatch(
      showModal({
        name: 'DIRECTIONS_EDITING_MODAL',
        data: {
          onApprove: () => {
            takeToDirectionEditingMutationFn(application.id)
              ?.unwrap()
              .then(application => {
                if (application) navigateToDirections();
              })
              .catch(() => {
                dispatch(
                  createToast(terms.APPLICATION_STATUS_CHANGING_ERROR, 'danger')
                );
              })
              .finally(() => dispatch(hideModal()));
          },
        },
      })
    );
  };

  const withdrawApplication = () => {
    if (!application) return;
    withdrawApplicationMutationFn(application.id)
      .unwrap()
      .then(() => {
        dispatch(createToast(terms.APPLICATION_DELETED, 'success'));
        refetch();
      })
      .catch(() => {
        dispatch(createToast(terms.APPLICATION_DELETION_ERROR, 'danger'));
      });
  };

  const handleDownload = (docType: DocumentTemplateType, fileName: string) => {
    generateDocument({
      id: Number(applicationId),
      docType,
    })
      ?.unwrap()
      .then(result => {
        downloadFile(new File([result], `${fileName}.docx`));
      });
  };

  const isEpgu = userApplication?.createdFromEpgu;

  return (
    <>
      <FillingStatementsForm
        onSubmit={e => {
          e.preventDefault();
          onNextPageNavigate();
        }}
        className="download-files-form"
      >
        <header className="download-files-form__header">
          {userApplication?.createdFromEpgu ? (
            <p>{terms.TITLE_BLANK_EPGU}</p>
          ) : (
            <>
              <p>{terms.TITLE_BLANK_HEADER}</p>
              <p>{terms.TITLE_BLANK_EL1}</p>
              <p>{terms.TITLE_BLANK_EL2}</p>
              <p>{terms.TITLE_BLANK_EL3}</p>
            </>
          )}
        </header>

        <DocumentList
          documents={documents?.filter(
            document =>
              ![
                DocumentType.ENROLL_AGREEMENT_DOCUMENT,
                DocumentType.ENROLL_WITHDRAW_DOCUMENT,
              ].includes(document.type)
          )}
          flag={isDocumentsDisabled}
          refetchDocuments={refetchDocuments}
          isEpgu={isEpgu}
        />

        {fields.map((field, index) => (
          <Checkbox
            key={field.id}
            text={field.label}
            {...register(`checkboxes.${index}.checked`, {
              onChange: onCheckboxChange,
            })}
            disabled={isFormDisabled}
          />
        ))}
      </FillingStatementsForm>
      {isImpersonate && (
        <div className="download-files-form__impersonate-controls">
          {application?.state === ApplicationState.UNDER_CONSIDERATION && (
            <Button
              type="button"
              theme="success"
              onClick={event => {
                event.stopPropagation();
                takeApplicationBack();
              }}
            >
              {terms.EDIT_APPLICATION}
            </Button>
          )}

          {(application?.state === ApplicationState.ALLOWED ||
            application?.state === ApplicationState.ACCEPTED) && (
            <>
              {application.educationLevel !==
                ApplicationEducationLevel.PREPARATORY && (
                <Button
                  type="button"
                  theme="success"
                  onClick={event => {
                    event.stopPropagation();
                    onDirectionsEditingClick();
                  }}
                >
                  {terms.EDIT_FIELDS}
                </Button>
              )}
              <Button
                type="button"
                className="statements-container__element-btn-panel_icon"
                onClick={event => {
                  event.stopPropagation();
                  withdrawApplication();
                }}
              >
                {terms.WITHDRAW_APPLICATION}
              </Button>
            </>
          )}

          {application?.state === ApplicationState.DIRECTIONS_EDITING && (
            <>
              <Button
                type="button"
                onClick={event => {
                  event.stopPropagation();
                  navigateToDirections();
                }}
              >
                {terms.EDIT_FIELDS}
              </Button>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default DownloadFilesForm;
